"use client";

import clsx from "clsx";
import { Tooltip } from "react-tooltip";

import { Column, Row } from "@/components/containers";
import { ImageLegacy, TooltipIcon } from "@/components/displays";
import { ConvenienceStoreImages } from "@/components/domains/checkout";
import { PanelSelector } from "@/components/inputs";
import { PaymentMethod } from "@/models/payment/consts";

import styles from "./PaymentMethodSelector.module.scss";
import { CreditCardForm } from "../CreditCardForm";

export interface PaymentData {
  paymentMethod?: PaymentMethod;
  // ここから下はStripeの情報
  stripePaymentMethodId?: string;
  last4?: string;
}

interface PaymentMethodSelectorProps {
  paymentData?: PaymentData;
  onChangePaymentData: (paymentData: PaymentData) => void;
  showConvenience?: boolean;
  showBankTransfer?: boolean;
  showDaibiki?: boolean;
  daibikiDisabledReason?: string;
}

const defaultPaymentData: PaymentData = {};

export function PaymentMethodSelector({
  paymentData = defaultPaymentData,
  onChangePaymentData,
  showConvenience = false,
  showBankTransfer = false,
  showDaibiki = false,
  daibikiDisabledReason,
}: PaymentMethodSelectorProps): React.ReactNode {
  return (
    <PanelSelector
      value={paymentData.paymentMethod}
      onChange={(paymentMethod) => onChangePaymentData({ ...paymentData, paymentMethod })}
      options={[
        {
          title: (
            <div className={styles.imageLabelContainer}>
              クレジットカード支払い
              <div className={styles.payment__image}>
                <ImageLegacy
                  src="https://asset.basefood.co.jp/images/parts/card_brands.png"
                  alt="クレジットカード"
                  size={{ width: 167, height: 24 }}
                />
              </div>
            </div>
          ),
          value: PaymentMethod.credit,
          content: (
            <Column
              className={styles.creditCardFormContainer}
              data-testid={`PaymentMethodSelector-inputCreditCard-column`}
            >
              <CreditCardForm />
            </Column>
          ),
          "data-testid": `PaymentMethodSelector-inputCreditCard-panelSelectorItem`,
        },
        showConvenience && {
          title: (
            <div className={styles.imageLabelContainer}>
              コンビニ前払い
              <ConvenienceStoreImages />
            </div>
          ),
          value: PaymentMethod.konbini,
        },
        showBankTransfer && {
          title: (
            <Row>
              銀行振込
              <i
                id="bank-transfer-fee-tooltip"
                className={clsx("fas fa-question-circle text__m", styles.questionIcon)}
              />
              <Tooltip anchorSelect="#bank-transfer-fee-tooltip">
                銀行指定の振込手数料はお客さま負担となります
              </Tooltip>
            </Row>
          ),
          value: PaymentMethod.ginkou,
        },
        {
          title: "Amazon Pay",
          value: PaymentMethod.amazon,
        },
        showDaibiki && {
          title: (
            <Row>
              代金引換（手数料330円〜
              <TooltipIcon
                tooltipTitle="代金引換の手数料"
                tooltipCloseButtonText="OK"
                tooltips={
                  <p>
                    9,670円未満のご購入：手数料330円
                    <br />
                    9,670円～29,559円のご購入：手数料440円
                    <br />
                    29,560~54,339円のご購入：手数料660円
                    <br />
                    54,340円以上のご購入：手数料880円
                    <br />
                    ※配送費含む
                  </p>
                }
                pcTooltips={`9,670円未満のご購入：手数料330円\n9,670円～29,559円のご購入：手数料440円\n29,560~54,339円のご購入：手数料660円\n54,340円以上のご購入：手数料880円\n※配送費含む`}
              />
              ）
            </Row>
          ),
          value: PaymentMethod.daibiki,
          disabled: !!daibikiDisabledReason,
          "data-testid": `PaymentMethodSelector-inputDaibiki-panelSelectorItem`,
        },
      ]}
      disabledReason={daibikiDisabledReason}
    />
  );
}
